import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { PortalService } from '../../services/portal.service';
import { UtilsService } from '../../services/utils.service';
import { CONSTANTS_TOKEN } from '../../config';
import { SnackBar } from '../../enums';
import { debounceTime, Subject } from 'rxjs';

export interface EditGroupDialogData {
  groupId: number;
  groupName: string;
}

export interface EditGroupDialogResult {
  action: boolean;
  groupName?: string;
  addedUserIds?: number[];
  removedUserIds?: number[];
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  entity_name?: string;
  isSelected: boolean;
  isOriginalMember: boolean;
}

@Component({
  selector: 'educare-edit-group-dialog',
  standalone: true,
  templateUrl: './edit-group-dialog.component.html',
  styleUrl: './edit-group-dialog.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    TranslateModule
  ]
})
export class EditGroupDialogComponent implements OnInit {
  public readonly dialogRef = inject(MatDialogRef<EditGroupDialogComponent>);
  public readonly data = inject<EditGroupDialogData>(MAT_DIALOG_DATA);
  private readonly portalService = inject(PortalService);
  private readonly utils = inject(UtilsService);

  groupName: string = this.data.groupName;
  searchTerm: string = '';
  users: User[] = [];
  filteredUsers: User[] = [];
  loading: boolean = false;
  loadingMore: boolean = false;
  isSubmitting: boolean = false;
  totalUsersCount: number = 0;
  loadedUsersCount: number = 0;
  totalMembersCount: number = 0;
  loadedMembersCount: number = 0;
  memberIds: Set<string> = new Set();

  private searchSubject = new Subject<string>();

  constructor(@Inject(CONSTANTS_TOKEN) private readonly constants: any) {}

  ngOnInit(): void {
    this.loadUsers();

    // Setup search debounce
    this.searchSubject.pipe(debounceTime(300)).subscribe((searchTerm) => {
      this.filterUsers(searchTerm);
    });
  }

  /**
   * Load all users and mark current group members with lazy loading
   */
  loadUsers(): void {
    this.loading = true;
    this.loadingMore = false;
    this.users = [];
    this.filteredUsers = [];
    this.memberIds.clear();

    // Load first page of users and first page of members in parallel
    this.loadUsersPage(1, true);
    this.loadMembersPage(1, true);
  }

  /**
   * Load a specific page of users
   */
  private loadUsersPage(page: number, isFirstPage: boolean = false): void {
    const allUsersUrl = this.utils.getEncryptedUrls(
      this.constants.api.privateEndpoints.users,
      `page=${page}&limit=100&is_profile_completed=1&is_active=1&is_email_verified=1`
    );

    this.portalService.get(allUsersUrl).subscribe({
      next: (response: any) => {
        const users = response?.data?.user || [];
        const totalCount = response?.data?.count || 0;

        this.totalUsersCount = totalCount;
        this.loadedUsersCount += users.length;

        // Map users and add to existing list
        const mappedUsers = users.map((user: any) => ({
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          entity_name: user.entity_name,
          isSelected: this.memberIds.has(String(user.id)),
          isOriginalMember: this.memberIds.has(String(user.id))
        }));

        this.users = [...this.users, ...mappedUsers];
        this.filteredUsers = [...this.users];

        // If first page, hide main loading and show background loading
        if (isFirstPage) {
          this.loading = false;
          this.loadingMore = this.loadedUsersCount < totalCount;
        }

        // Continue loading if there are more pages
        if (this.loadedUsersCount < totalCount) {
          this.loadUsersPage(page + 1, false);
        } else {
          this.loadingMore = false;
        }
      },
      error: () => {
        if (isFirstPage) {
          this.loading = false;
        }
        this.loadingMore = false;
        this.utils.openSnackBar('Failed to load users. Please try again.', 'Close', SnackBar.Error);
      }
    });
  }

  /**
   * Load a specific page of group members
   */
  private loadMembersPage(page: number, isFirstPage: boolean = false): void {
    const membersUrl = this.utils.getEncryptedUrls(
      this.constants.api.privateEndpoints.groups,
      `/${this.data.groupId}/members?page=${page}&limit=100`
    );

    this.portalService.get(membersUrl).subscribe({
      next: (membersResponse: any) => {
        const members = membersResponse?.data?.members || [];
        const totalCount = membersResponse?.data?.count || 0;

        // Update total count only if it's provided and greater than current
        if (totalCount > 0) {
          this.totalMembersCount = totalCount;
        }
        this.loadedMembersCount += members.length;

        // Add member IDs to the set
        members.forEach((member: any) => {
          this.memberIds.add(String(member.id));
        });

        // Update existing users to reflect member status
        this.users = this.users.map((user) => ({
          ...user,
          isSelected: this.memberIds.has(String(user.id)),
          isOriginalMember: this.memberIds.has(String(user.id))
        }));
        this.filteredUsers = [...this.users];

        // Continue loading if there are more members
        // Stop when: no more members returned OR loaded count >= total count
        if (members.length > 0) {
          if (this.totalMembersCount === 0 || this.loadedMembersCount < this.totalMembersCount) {
            this.loadMembersPage(page + 1, false);
          }
        }
      },
      error: () => {
        if (isFirstPage) {
          this.loading = false;
        }
        this.utils.openSnackBar('Failed to load group members. Please try again.', 'Close', SnackBar.Error);
      }
    });
  }

  /**
   * Handle search input change
   */
  onSearchChange(searchTerm: string): void {
    this.searchSubject.next(searchTerm);
  }

  /**
   * Filter users based on search term
   */
  filterUsers(searchTerm: string): void {
    if (!searchTerm.trim()) {
      this.filteredUsers = [...this.users];
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredUsers = this.users.filter(
      (user) =>
        user.first_name.toLowerCase().includes(term) ||
        user.last_name.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        (user.entity_name && user.entity_name.toLowerCase().includes(term))
    );
  }

  /**
   * Handle user selection change from checkbox
   */
  onUserSelectionChange(user: User, checked: boolean): void {
    user.isSelected = checked;
  }

  /**
   * Get selected users count
   */
  get selectedCount(): number {
    return this.users.filter((u) => u.isSelected).length;
  }

  /**
   * Check if update button should be enabled
   */
  get isUpdateEnabled(): boolean {
    // Check if group name changed
    if (this.groupName.trim() !== this.data.groupName) {
      return true;
    }

    // Check if any user selection changed
    const hasChanges = this.users.some((user) => user.isSelected !== user.isOriginalMember);

    return hasChanges;
  }

  /**
   * Submit the form
   */
  onSubmit(): void {
    if (this.isSubmitting || !this.isUpdateEnabled) {
      return;
    }

    this.isSubmitting = true;

    // Calculate added and removed users
    const addedUserIds: number[] = [];
    const removedUserIds: number[] = [];

    this.users.forEach((user) => {
      if (user.isSelected && !user.isOriginalMember) {
        addedUserIds.push(user.id);
      } else if (!user.isSelected && user.isOriginalMember) {
        removedUserIds.push(user.id);
      }
    });

    const result: EditGroupDialogResult = {
      action: true,
      groupName: this.groupName.trim(),
      addedUserIds,
      removedUserIds
    };

    this.dialogRef.close(result);
  }

  /**
   * Cancel and close dialog
   */
  onCancel(): void {
    this.dialogRef.close({ action: false });
  }

  /**
   * Close dialog
   */
  onClose(): void {
    this.dialogRef.close({ action: false });
  }

  /**
   * Get user full name
   */
  getUserFullName(user: User): string {
    return `${user.first_name} ${user.last_name}`;
  }
}
